import { Button, Select, SelectItem, Spinner } from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';

export interface RemoteSettlementDocument {
  fileName: string;
  url: string;
  mimeType?: string;
}

interface SettlementSourcePreviewProps {
  file?: File;
  remoteDocument?: RemoteSettlementDocument;
  onClose: () => void;
}

const MAX_EXCEL_ROWS = 500;
const MAX_EXCEL_COLUMNS = 60;

const getFileKind = (
  fileName: string,
  mimeType = ''
): 'pdf' | 'excel' | 'unsupported' => {
  const normalizedName = fileName.toLowerCase();
  if (mimeType === 'application/pdf' || normalizedName.endsWith('.pdf')) {
    return 'pdf';
  }
  if (normalizedName.endsWith('.xlsx') || normalizedName.endsWith('.xls')) {
    return 'excel';
  }
  return 'unsupported';
};

const formatCellValue = (value: unknown): string => {
  if (value == null) return '';
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const SettlementSourcePreview = ({
  file,
  remoteDocument,
  onClose,
}: SettlementSourcePreviewProps) => {
  const fileName = file?.name || remoteDocument?.fileName || 'Documento';
  const mimeType = file?.type || remoteDocument?.mimeType || '';
  const fileKind = useMemo(
    () => getFileKind(fileName, mimeType),
    [fileName, mimeType]
  );
  const [pdfUrl, setPdfUrl] = useState('');
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPdfUrl('');
    setWorkbook(null);
    setSelectedSheet('');
    setError('');

    if (fileKind === 'pdf' && file) {
      const objectUrl = URL.createObjectURL(file);
      setPdfUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    if (fileKind === 'pdf' && remoteDocument) {
      setPdfUrl(remoteDocument.url);
      return;
    }

    if (fileKind !== 'excel') return;

    let active = true;
    setIsLoading(true);
    const loadWorkbook = file
      ? file.arrayBuffer()
      : fetch(remoteDocument?.url || '').then((response) => {
          if (!response.ok) {
            throw new Error(`Could not load workbook (${response.status})`);
          }
          return response.arrayBuffer();
        });

    void loadWorkbook
      .then((data) => {
        const nextWorkbook = XLSX.read(data, {
          type: 'array',
          cellDates: true,
        });
        if (!active) return;
        setWorkbook(nextWorkbook);
        setSelectedSheet(nextWorkbook.SheetNames[0] || '');
      })
      .catch((previewError) => {
        console.error('Error reading source workbook preview:', previewError);
        if (active) setError('No se pudo mostrar la vista previa del Excel.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [file, fileKind, remoteDocument]);

  const excelPreview = useMemo(() => {
    if (!workbook || !selectedSheet) return null;
    const worksheet = workbook.Sheets[selectedSheet];
    if (!worksheet) return null;

    const usedRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const totalRowCount = usedRange.e.r + 1;
    const totalColumnCount = usedRange.e.c + 1;
    const columnCount = Math.min(totalColumnCount, MAX_EXCEL_COLUMNS);
    const previewRange = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: {
        r: Math.min(usedRange.e.r, MAX_EXCEL_ROWS - 1),
        c: Math.min(usedRange.e.c, MAX_EXCEL_COLUMNS - 1),
      },
    });
    const allRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
      header: 1,
      raw: false,
      defval: '',
      range: previewRange,
    }) as unknown[][];

    return {
      rows: allRows.map((row) => row.slice(0, columnCount)),
      columnCount,
      totalRowCount,
      totalColumnCount,
    };
  }, [selectedSheet, workbook]);

  const openOriginal = () => {
    const url =
      pdfUrl || remoteDocument?.url || (file && URL.createObjectURL(file));
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
    if (!pdfUrl && !remoteDocument) {
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
  };

  return (
    <aside className='flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-divider bg-content1 shadow-sm'>
      <div className='flex flex-wrap items-center justify-between gap-2 border-b border-divider px-3 py-2'>
        <div className='flex min-w-0 items-center gap-2'>
          {fileKind === 'pdf' ? (
            <Icon icon='tabler:file-type-pdf' className='shrink-0 text-danger' />
          ) : (
            <Icon icon='tabler:file-spreadsheet' className='shrink-0 text-success' />
          )}
          <div className='min-w-0'>
            <p className='truncate text-sm font-semibold'>Archivo original</p>
            <p className='truncate text-xs text-default-500'>{fileName}</p>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label='Abrir archivo original en otra pestaña'
            onPress={openOriginal}
          >
            <Icon icon='tabler:external-link' />
          </Button>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label='Ocultar archivo original'
            onPress={onClose}
          >
            <Icon icon='tabler:x' />
          </Button>
        </div>
      </div>

      {fileKind === 'pdf' && pdfUrl && (
        <iframe
          title={`Archivo original ${fileName}`}
          src={pdfUrl}
          className='min-h-0 flex-1 border-0 bg-content1'
        />
      )}

      {fileKind === 'excel' && (
        <>
          <div className='flex flex-wrap items-center justify-between gap-2 border-b border-divider px-3 py-2'>
            <Select
              size='sm'
              label='Hoja'
              className='max-w-xs'
              selectedKeys={selectedSheet ? [selectedSheet] : []}
              onSelectionChange={(keys) =>
                setSelectedSheet(String(Array.from(keys)[0] || ''))
              }
              isDisabled={!workbook || workbook.SheetNames.length <= 1}
            >
              {(workbook?.SheetNames || []).map((sheetName) => (
                <SelectItem key={sheetName} value={sheetName}>
                  {sheetName}
                </SelectItem>
              ))}
            </Select>
            {excelPreview &&
              (excelPreview.totalRowCount > MAX_EXCEL_ROWS ||
                excelPreview.totalColumnCount > MAX_EXCEL_COLUMNS) && (
                <p className='text-xs text-warning-600'>
                  Vista limitada a {MAX_EXCEL_ROWS} filas y {MAX_EXCEL_COLUMNS}{' '}
                  columnas
                </p>
              )}
          </div>

          {isLoading && (
            <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-sm text-default-500'>
              <Spinner size='sm' />
              <span>Leyendo Excel…</span>
            </div>
          )}

          {!isLoading && excelPreview && (
            <div className='min-h-0 flex-1 overflow-auto bg-default-50'>
              <table className='border-separate border-spacing-0 text-xs'>
                <thead className='sticky top-0 z-20 bg-default-100'>
                  <tr>
                    <th className='sticky left-0 z-30 min-w-12 border-b border-r border-divider bg-default-100 px-2 py-1 text-default-500'>
                      #
                    </th>
                    {Array.from(
                      { length: excelPreview.columnCount },
                      (_, columnIndex) => (
                        <th
                          key={columnIndex}
                          className='min-w-28 border-b border-r border-divider bg-default-100 px-2 py-1 text-center font-medium text-default-500'
                        >
                          {XLSX.utils.encode_col(columnIndex)}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {excelPreview.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <th className='sticky left-0 z-10 border-b border-r border-divider bg-default-100 px-2 py-1 text-right font-medium text-default-500'>
                        {rowIndex + 1}
                      </th>
                      {Array.from(
                        { length: excelPreview.columnCount },
                        (_, columnIndex) => (
                          <td
                            key={columnIndex}
                            className='max-w-64 border-b border-r border-divider bg-content1 px-2 py-1 align-top whitespace-pre-wrap break-words'
                          >
                            {formatCellValue(row[columnIndex])}
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {(error || fileKind === 'unsupported') && (
        <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm text-default-500'>
          <p>{error || 'Este tipo de archivo no admite vista previa.'}</p>
          <Button size='sm' variant='flat' onPress={openOriginal}>
            Abrir archivo original
          </Button>
        </div>
      )}
    </aside>
  );
};

export default SettlementSourcePreview;
